/**
 * Manual samples: the table where the first population of an exploration is
 * typed in by hand (one row per sample, one column per design-space
 * parameter), the parameters the filler samples may vary and the switch that
 * lets the optimizer continue from the typed population.
 *
 * Cells are validated against the laser's value grid; an off-grid value is
 * flagged with the nearest allowed value and only "Snap all to grid" applies
 * it. The rows are owned by the parent (strings, as typed).
 */
import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DesignPointsErrorAlert, { parameterLabel } from "./DesignPointsErrorAlert";
import {
  cellMessage,
  defaultRow,
  fillSummary,
  parsePastedRows,
  snapRowsToGrid,
  validateRows,
} from "../helper/manualSamples";


function gridHint(spec) {
  const first = spec.values[0];
  const last = spec.values[spec.values.length - 1];
  return `${first} – ${last}, ${spec.values.length} values`;
}

export default function ManualSamplesEditor({
  specs,
  rows,
  onRowsChange,
  vary,
  onVaryChange,
  continueWithOptimizer,
  onContinueChange,
  populationSize,
  gridSize,
  serverError,
}) {
  const validation = validateRows(rows, specs);
  const perBatch =
    Array.isArray(gridSize) && gridSize.length === 2 ? Number(gridSize[0]) * Number(gridSize[1]) : null;
  const populationKnown = Number.isInteger(populationSize) && populationSize > 0;
  const populationFitsGrid = !perBatch || !populationKnown || populationSize % perBatch === 0;
  const needsFill = populationKnown && rows.length < populationSize;
  const tooMany = populationKnown && rows.length > populationSize;

  const updateCell = (rowIndex, name, text) => {
    onRowsChange(rows.map((row, index) => (index === rowIndex ? { ...row, [name]: text } : row)));
  };
  const addRow = () => onRowsChange([...rows, defaultRow(specs)]);
  const duplicateRow = (rowIndex) => {
    const next = [...rows];
    next.splice(rowIndex + 1, 0, { ...rows[rowIndex] });
    onRowsChange(next);
  };
  const deleteRow = (rowIndex) => onRowsChange(rows.filter((_, index) => index !== rowIndex));
  const snapAll = () => onRowsChange(snapRowsToGrid(rows, specs));
  const clearRows = () => onRowsChange([]);
  const toggleVary = (name) => {
    onVaryChange(vary.includes(name) ? vary.filter((v) => v !== name) : [...vary, name]);
  };
  const handlePaste = (event) => {
    const text = event.clipboardData?.getData("text") || "";
    // A single value pastes into its cell; several cells or lines become rows.
    if (!/[\t\n;,]/.test(text)) return;
    const pasted = parsePastedRows(text, specs);
    if (pasted.length === 0) return;
    event.preventDefault();
    onRowsChange([...rows, ...pasted]);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        One row per sample, values on the laser&apos;s grid. Paste rows from a spreadsheet in this
        column order.
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" aria-label="Manual samples">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "grey.50", whiteSpace: "nowrap" } }}>
              <TableCell>#</TableCell>
              {specs.map((spec) => (
                <TableCell key={spec.name}>
                  <Tooltip title={gridHint(spec)}>
                    <span>{parameterLabel(spec.name)}</span>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody onPaste={handlePaste}>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={specs.length + 2}>
                  <Typography variant="body2" color="text.secondary">
                    No samples yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((row, rowIndex) => {
              const duplicateOf = validation.duplicateOf[rowIndex];
              return (
                <TableRow key={rowIndex}>
                  <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top", pt: 1.5 }}>
                    {rowIndex + 1}
                    {duplicateOf !== null ? (
                      <Typography variant="caption" color="error" display="block">
                        duplicate of {duplicateOf + 1}
                      </Typography>
                    ) : null}
                  </TableCell>
                  {specs.map((spec) => {
                    const cell = validation.cells[rowIndex][spec.name];
                    const message = cellMessage(cell, spec);
                    return (
                      <TableCell key={spec.name} sx={{ verticalAlign: "top" }}>
                        <TextField
                          size="small"
                          variant="standard"
                          value={row[spec.name] ?? ""}
                          error={Boolean(message)}
                          helperText={message}
                          inputProps={{
                            "aria-label": `${parameterLabel(spec.name)} of sample ${rowIndex + 1}`,
                            inputMode: "decimal",
                          }}
                          onChange={(event) => updateCell(rowIndex, spec.name, event.target.value)}
                          sx={{ width: 96 }}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                    <Tooltip title="Duplicate">
                      <IconButton size="small" aria-label={`Duplicate sample ${rowIndex + 1}`} onClick={() => duplicateRow(rowIndex)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" aria-label={`Delete sample ${rowIndex + 1}`} onClick={() => deleteRow(rowIndex)}>
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: "none" }}>
          Add row
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          disabled={validation.offGridCount === 0}
          onClick={snapAll}
          sx={{ textTransform: "none" }}
        >
          Snap all to grid
        </Button>
        <Button size="small" variant="text" disabled={rows.length === 0} onClick={clearRows} sx={{ textTransform: "none" }}>
          Clear rows
        </Button>
      </Stack>

      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {populationKnown
            ? `Population ${populationSize} (from the Optimization card) — ${fillSummary(rows.length, populationSize)}`
            : "Population size not set on the Optimization card."}
        </Typography>
        {tooMany ? (
          <Typography variant="caption" color="error" display="block">
            Remove {rows.length - populationSize} row{rows.length - populationSize === 1 ? "" : "s"} or raise the
            population size.
          </Typography>
        ) : null}
        {!populationFitsGrid ? (
          <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
            The population size ({populationSize}) must be a multiple of the patch group grid ({gridSize[0]} ×{" "}
            {gridSize[1]} = {perBatch}).
          </Alert>
        ) : null}
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Fill remaining samples by varying
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Each filler copies a typed sample and changes exactly one of these parameters to another allowed
          value. All samples stay distinct.
        </Typography>
        <FormGroup row>
          {specs.map((spec) => (
            <FormControlLabel
              key={spec.name}
              control={
                <Checkbox
                  size="small"
                  checked={vary.includes(spec.name)}
                  disabled={!needsFill || spec.values.length < 2}
                  onChange={() => toggleVary(spec.name)}
                  inputProps={{ "aria-label": `Vary ${parameterLabel(spec.name)}` }}
                />
              }
              label={parameterLabel(spec.name)}
            />
          ))}
        </FormGroup>
        {needsFill && vary.length === 0 ? (
          <Typography variant="caption" color="error" display="block">
            Pick at least one parameter.
          </Typography>
        ) : null}
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(continueWithOptimizer)}
              onChange={(event) => onContinueChange(event.target.checked)}
              inputProps={{ "aria-label": "Continue with the optimizer after measurement" }}
            />
          }
          label="Continue with the optimizer after measurement"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          {continueWithOptimizer
            ? "On: the optimizer breeds the next generation from the typed population."
            : "Off: the exploration completes once these samples are measured."}
        </Typography>
      </Box>

      <DesignPointsErrorAlert error={serverError} fallback="The manual start was refused." />
    </Stack>
  );
}
